import React from 'react';
import HeroSection from '../components/HeroSection';
import ServicesSection from '../components/ServicesSection';
import FeaturesSection from '../components/FeaturesSection';
import TeamSection from '../components/TeamSection';
import RatingsSection from '../components/RatingsSection';
import ContactSection from '../components/ContactSection';
import PortfolioSection from '../components/PortfolioSection';
import TestimonialsSection from '../components/TestimonialsSection';

/**
 * MonkeyTech — Home (Responsive + Themed)
 * - Snap scrolling only from md+ for better mobile UX
 * - Background orbs scale down on small screens
 * - Compact dividers & spacing on mobile
 */

export default function Home() {
  return (
    <main
      role="main"
      aria-label="MonkeyTech Homepage"
      className="
        relative min-h-screen font-inter
        md:snap-y md:snap-mandatory scroll-smooth
        text-white
      "
      style={{
        // Brand tokens
        '--brand': '#1FA550',
        '--brand-alt': '#50AD69',
        '--ink': '#0F1211',
        '--paper': '#DEE0E3',
        '--accent': '#06B6D4',
        '--ring': 'rgba(31,165,80,0.55)',
        '--brand-rgb': '31,165,80',
      }}
    >
      {/* Background layers */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        {/* Dark tech base */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1220] via-[#0B1325] to-black" />
        {/* Brand glow left (smaller on mobile) */}
        <div className="
          absolute -top-20 -left-10 rounded-full blur-3xl
          h-[18rem] w-[18rem] sm:h-[26rem] sm:w-[26rem] lg:h-[34rem] lg:w-[34rem]
          bg-[radial-gradient(closest-side,rgba(31,165,80,0.24),transparent)]
        " />
        {/* Accent glow right */}
        <div className="
          absolute top-1/3 -right-10 rounded-full blur-3xl
          h-[16rem] w-[16rem] sm:h-[22rem] sm:w-[22rem] lg:h-[30rem] lg:w-[30rem]
          bg-[radial-gradient(closest-side,rgba(6,182,212,0.16),transparent)]
        " />
        {/* Soft paper sheen */}
        <div className="absolute inset-x-0 top-0 h-16 sm:h-20 lg:h-24 bg-gradient-to-b from-white/5 to-transparent" />
      </div>

      {/* Skip link for accessibility */}
      <a
        href="#services"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:px-3 focus:py-2 focus:bg-black/70 focus:text-white rounded-md z-50"
      >
        Skip to services
      </a>

      {/* Order: Hero → Services → Features → Portfolio → Testimonials → Ratings → Team → Contact */}
      <section id="hero" className="snap-start scroll-mt-16">
        <HeroSection />
      </section>

      <SectionDivider label="What we do" />
      <section id="services" className="snap-start scroll-mt-16 py-6 sm:py-8 lg:py-12">
        <ServicesSection />
      </section>

      <SectionDivider label="How it works" subtle />
      <section id="features" className="snap-start scroll-mt-16 py-6 sm:py-8 lg:py-12">
        <FeaturesSection />
      </section>

      <SectionDivider label="Work we love" />
      <section id="portfolio" className="snap-start scroll-mt-16 py-6 sm:py-8 lg:py-12">
        <PortfolioSection />
      </section>

      {/* Optional: show testimonials between work and impact */}
      <SectionDivider label="What clients say" subtle />
      <section id="testimonials" className="snap-start scroll-mt-16 py-6 sm:py-8 lg:py-12">
        <TestimonialsSection />
      </section>

      <SectionDivider label="Impact" />
      <section id="ratings" className="snap-start scroll-mt-16 py-6 sm:py-8 lg:py-12">
        <RatingsSection />
      </section>

      <SectionDivider label="Team" subtle />
      <section id="team" className="snap-start scroll-mt-16 py-6 sm:py-8 lg:py-12">
        <TeamSection />
      </section>

      <SectionDivider label="Let’s build" />
      <section id="contact" className="snap-start scroll-mt-16 py-8 sm:py-10 lg:py-16">
        <ContactSection />
      </section>

      {/* Global styles for brand typography */}
      <style>{`
        .font-inter { font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial; }
      `}</style>
    </main>
  );
}

/* --- Section divider chip (responsive) --- */
function SectionDivider({ label = '', subtle = false }) {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="relative flex items-center gap-2 sm:gap-3 pt-4 sm:pt-6">
        <div className={`h-px flex-1 ${subtle ? 'bg-white/10' : 'bg-white/20'}`} />
        <span
          className={`
            inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[10px] sm:text-xs font-semibold ring-1 backdrop-blur
            ${subtle
              ? 'bg-white/5 text-white/80 ring-white/15'
              : 'bg-[rgba(31,165,80,0.12)] text-white ring-[rgba(31,165,80,0.35)]'}
          `}
        >
          <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-[var(--brand)]" />
          {label}
        </span>
        <div className={`h-px flex-1 ${subtle ? 'bg-white/10' : 'bg-white/20'}`} />
      </div>
    </div>
  );
}
